import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  approveMissionControlApproval,
  cancelMissionControlRun,
  getMissionControlRun,
  listMissionControlApprovals,
  listMissionControlRuns,
  rejectMissionControlApproval,
  type MissionControlProcessRunRef,
} from '@/api/mission-control-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUser } from '@/hooks/useUser';

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) {
      return response.data.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Request failed. Please retry.';
}

function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function formatExpiry(expiresAt: string): string {
  const remainingMs = new Date(expiresAt).getTime() - Date.now();
  if (remainingMs <= 0) {
    return 'expired';
  }
  const days = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
  return days === 1 ? 'expires in 1 day' : `expires in ${days} days`;
}

const CANCELLABLE_PROCESS_RUN_STATUSES = new Set(['QUEUED', 'RUNNING', 'WAITING_APPROVAL']);

function canCancelProcessRun(ref: MissionControlProcessRunRef | null): ref is MissionControlProcessRunRef {
  return ref !== null && CANCELLABLE_PROCESS_RUN_STATUSES.has(ref.status);
}

function outcomeWord(outcome: string): string {
  return outcome.replace(/_/g, ' ');
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'SUCCEEDED' || status === 'EXECUTED' || status === 'APPROVED') {
    return 'default';
  }
  if (status === 'CANCELLED') {
    return 'outline';
  }
  if (status === 'FAILED' || status === 'REJECTED' || status === 'EXPIRED') {
    return 'destructive';
  }
  if (status === 'PENDING_APPROVAL' || status === 'PENDING' || status === 'WAITING_APPROVAL') {
    return 'secondary';
  }
  return 'outline';
}

export function AdminMissionControl() {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [cancelOutcome, setCancelOutcome] = useState<{ runId: string; outcome: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTabFromUrl = searchParams.get('mcTab');
  const runIdFromUrl = searchParams.get('runId');
  const initialTab = currentTabFromUrl === 'approvals' ? 'approvals' : 'runs';
  const [activeTab, setActiveTab] = useState<'runs' | 'approvals'>(initialTab);
  const queryClient = useQueryClient();
  const memberContextQuery = useUser();
  const canDecideApprovals = Boolean(
    memberContextQuery.data?.selectedMember.isAdmin && !memberContextQuery.data?.selectedMember.isPersonal
  );

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const runsQuery = useQuery({
    queryKey: ['mission-control-runs'],
    queryFn: () => listMissionControlRuns(25),
  });

  const approvalsQuery = useQuery({
    queryKey: ['mission-control-approvals'],
    queryFn: listMissionControlApprovals,
  });

  useEffect(() => {
    if (!selectedRunId && runsQuery.data?.items.length) {
      setSelectedRunId(runsQuery.data.items[0].id);
    }
  }, [runsQuery.data, selectedRunId]);

  useEffect(() => {
    if (!runIdFromUrl || !runsQuery.data?.items.length) {
      return;
    }
    const exists = runsQuery.data.items.some((run) => run.id === runIdFromUrl);
    if (exists) {
      setSelectedRunId(runIdFromUrl);
    }
  }, [runIdFromUrl, runsQuery.data]);

  const runDetailQuery = useQuery({
    queryKey: ['mission-control-run', selectedRunId],
    queryFn: () => getMissionControlRun(selectedRunId as string),
    enabled: Boolean(selectedRunId),
  });

  const refreshMissionControl = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['mission-control-runs'] }),
      queryClient.invalidateQueries({ queryKey: ['mission-control-approvals'] }),
      queryClient.invalidateQueries({ queryKey: ['mission-control-run', selectedRunId] }),
    ]);
  };

  const approveMutation = useMutation({
    mutationFn: (approvalId: string) => approveMissionControlApproval(approvalId),
    onSuccess: async () => {
      setMutationError(null);
      await refreshMissionControl();
    },
    onError: (error) => {
      setMutationError(getErrorMessage(error));
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (approvalId: string) => rejectMissionControlApproval(approvalId),
    onSuccess: async () => {
      setMutationError(null);
      await refreshMissionControl();
    },
    onError: (error) => {
      setMutationError(getErrorMessage(error));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (ref: MissionControlProcessRunRef) => cancelMissionControlRun(ref.id, ref.kind),
    onSuccess: async (result) => {
      setMutationError(null);
      setCancelOutcome({ runId: result.runId, outcome: outcomeWord(result.outcome) });
      await refreshMissionControl();
    },
    onError: (error) => {
      setMutationError(getErrorMessage(error));
    },
  });

  const renderCancelAction = (processRun: MissionControlProcessRunRef | null) => {
    const outcome = processRun !== null && cancelOutcome?.runId === String(processRun.id) ? cancelOutcome.outcome : null;
    if (!canCancelProcessRun(processRun) && outcome === null) {
      return null;
    }
    return (
      <div className="flex items-center gap-2">
        {canCancelProcessRun(processRun) ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => cancelMutation.mutate(processRun)}
            disabled={cancelMutation.isPending}
          >
            Cancel
          </Button>
        ) : null}
        {outcome !== null ? <span className="text-xs text-muted-foreground">{outcome}</span> : null}
      </div>
    );
  };

  const pendingApprovals = useMemo(
    () => (approvalsQuery.data?.items ?? []).filter((item) => item.status === 'PENDING'),
    [approvalsQuery.data]
  );

  return (
    <div className="space-y-4">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <h2 className="text-xl font-light tracking-wide">Mission Control</h2>
          <p className="text-muted-foreground">
            Review run lifecycle, tool execution records, and approval queue outcomes.
          </p>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          const nextTab = value === 'approvals' ? 'approvals' : 'runs';
          setActiveTab(nextTab);
          const next = new URLSearchParams(searchParams);
          if (nextTab === 'approvals') {
            next.set('mcTab', 'approvals');
          } else {
            next.delete('mcTab');
          }
          setSearchParams(next, { replace: true });
        }}
        className="px-4 space-y-3"
      >
        <TabsList>
          <TabsTrigger value="runs">Runs</TabsTrigger>
          <TabsTrigger value="approvals">Approvals</TabsTrigger>
        </TabsList>

        <TabsContent value="runs" className="space-y-3">
          {mutationError ? (
            <p className="text-sm text-destructive">{mutationError}</p>
          ) : null}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="divide-y border rounded-md">
              {(runsQuery.data?.items ?? []).map((run) => (
                <div
                  key={run.id}
                  className={`p-3 transition-colors ${
                    selectedRunId === run.id ? 'bg-muted/50' : 'hover:bg-muted/40'
                  }`}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setSelectedRunId(run.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{run.agentKey}</p>
                      <div className="flex items-center gap-2">
                        {run.processRun ? (
                          <Badge variant={statusVariant(run.processRun.status)}>
                            {run.processRun.status}
                            {run.processRun.cancelReason ? ` · ${run.processRun.cancelReason}` : ''}
                          </Badge>
                        ) : null}
                        <Badge variant={statusVariant(run.status)}>{run.status}</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-1">{run.inputMessage}</p>
                  </button>
                  {renderCancelAction(run.processRun) ? <div className="mt-2">{renderCancelAction(run.processRun)}</div> : null}
                </div>
              ))}
            </div>

            <div className="rounded-md border p-3 space-y-3 bg-background">
              {runDetailQuery.data ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{runDetailQuery.data.agentKey}</p>
                    <div className="flex items-center gap-2">
                      {runDetailQuery.data.processRun ? (
                        <Badge variant={statusVariant(runDetailQuery.data.processRun.status)}>
                          Run {runDetailQuery.data.processRun.status}
                          {runDetailQuery.data.processRun.cancelReason ? ` · ${runDetailQuery.data.processRun.cancelReason}` : ''}
                        </Badge>
                      ) : null}
                      <Badge variant={statusVariant(runDetailQuery.data.status)}>{runDetailQuery.data.status}</Badge>
                    </div>
                  </div>
                  {renderCancelAction(runDetailQuery.data.processRun)}

                  <div className="space-y-1">
                    <p className="text-xs upperanchor text-muted-foreground">Reply</p>
                    <p className="text-sm whitespace-pre-wrap">{runDetailQuery.data.outputReply ?? 'No final reply yet.'}</p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs upperanchor text-muted-foreground">Tool Calls</p>
                    {runDetailQuery.data.toolCalls.map((call) => (
                      <details key={call.id} className="rounded border p-2">
                        <summary className="cursor-pointer text-sm font-medium flex items-center gap-2">
                          {call.toolKey} <Badge variant={statusVariant(call.status)}>{call.status}</Badge>
                        </summary>
                        <div className="mt-2 space-y-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Input</p>
                            <pre className="text-xs whitespace-pre-wrap">{formatJson(call.inputJson)}</pre>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Output / Error</p>
                            <pre className="text-xs whitespace-pre-wrap">{formatJson(call.outputJson ?? call.error ?? null)}</pre>
                          </div>
                          {call.approvalRequest ? (
                            <p className="text-xs text-muted-foreground">
                              Approval: {call.approvalRequest.status}
                            </p>
                          ) : null}
                        </div>
                      </details>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Select a run to inspect details.</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="approvals" className="space-y-2">
          {mutationError ? (
            <p className="text-sm text-destructive">{mutationError}</p>
          ) : null}
          {!canDecideApprovals && pendingApprovals.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              Only hub admins can approve or reject pending requests.
            </p>
          ) : null}
          {pendingApprovals.map((approval) => (
            <div key={approval.id} className="p-3 space-y-2 border rounded-md">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">{approval.toolCall.toolKey}</p>
                  <p className="text-xs text-muted-foreground truncate">{approval.toolCall.agentRun.inputMessage}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{formatExpiry(approval.expiresAt)}</span>
                  <Badge variant={statusVariant(approval.status)}>{approval.status}</Badge>
                </div>
              </div>

              {approval.presentation ? (
                <div className="space-y-2">
                  <p className="text-sm">{approval.presentation.summary}</p>
                  {approval.presentation.detail.length > 0 ? (
                    <dl className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-3 gap-y-1 text-xs">
                      {approval.presentation.detail.map((detail) => (
                        <Fragment key={detail.label}>
                          <dt className="text-muted-foreground">{detail.label}</dt>
                          <dd className="break-words whitespace-pre-wrap">{detail.value}</dd>
                        </Fragment>
                      ))}
                    </dl>
                  ) : null}
                </div>
              ) : (
                <pre className="text-xs whitespace-pre-wrap">{formatJson(approval.toolCall.inputJson)}</pre>
              )}

              {canDecideApprovals ? (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => approveMutation.mutate(approval.id)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rejectMutation.mutate(approval.id)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                  >
                    Reject
                  </Button>
                </div>
              ) : null}
            </div>
          ))}

          {pendingApprovals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending approvals in your current scope.</p>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
